import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $notificationStore, $transferRequestsApprovedListStore, $transferRequestsGridListStore, $transferUpdateStatusStore } from '@lib/store';
import { useListState, useRouteHook } from '@shared/hooks';
import { cn, DateUtils, FormatUtils, PaletteUtils, RoutePaths } from '@shared/utils';
import { TransferRequestStatusEnum, type TransferRequestResponse } from '@shared/api/rest/transferRequests';
import { ConfirmModal, OTPModal, TransferRequestConfirmModal } from '@widgets';
import { ColorsEnum, Icon, Pagination, Spinner, Text } from '@shared/widgets';
import ShareIcon from '@assets/icons/share3.svg';
import ConfirmIcon from '@assets/icons/confirm.svg';

interface ApprovePaymentsProps {
  className?: string;
}

export const ApprovePayments = (props: ApprovePaymentsProps) => {
  const { navigate } = useRouteHook();

  const transferStore = useUnit($transferRequestsApprovedListStore.store);

  const [showApprovalModal, setShowApprovalModal] = useState<TransferRequestResponse | null>(null);
  const [showOtpModal, setShowOtpModal] = useState<TransferRequestResponse | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<TransferRequestResponse | null>(null);
  const listState = useListState({ count: 5 });

  const { t } = useTranslation('payments');

  useEffect(() => {
    $transferRequestsApprovedListStore.events.update({
      status: TransferRequestStatusEnum.WaitingForApproval,
      FromIndex: listState.fromIndex,
      Count: listState.count,
    });
    $transferUpdateStatusStore.events.clear();
  }, [listState]);

  const submitHandler = async () => {
    await $transferRequestsGridListStore.events.update({ Count: 10, FromIndex: 0 });
    await $transferRequestsApprovedListStore.events.update({ status: TransferRequestStatusEnum.WaitingForApproval, Count: 5, FromIndex: 0 });
  };

  return (
    <>
      {(transferStore.loading || transferStore.data.data.length > 0) && (
        <div
          className={cn(
            'flex flex-col gap-3 p-6',
            PaletteUtils.isVcfcPalette && 'bg-ghost-white dark:bg-eerie-black',
            PaletteUtils.isInsigniaPalette && 'bg-white dark:bg-soft-black',
            props.className,
          )}
        >
          <Text weight={PaletteUtils.isInsigniaPalette ? 'semibold' : undefined} size={PaletteUtils.isInsigniaPalette ? '2xl' : 'xl'}>
            {t('approvePayments.title')}
          </Text>
          {transferStore.loading && <Spinner />}
          {!transferStore.loading && transferStore.data.data.length > 0 && (
            <>
              <div className='mb-auto flex flex-col gap-2'>
                {transferStore.data.data.map((transfer) => (
                  <div
                    key={transfer.id}
                    className={cn(
                      'flex cursor-pointer items-start justify-start bg-white p-3 transition-shadow duration-200 dark:bg-light/5',
                      'hover:shadow-lg dark:hover:shadow-lg-dark',
                      PaletteUtils.isInsigniaPalette && 'bg-ice',
                    )}
                    onClick={() => setShowApprovalModal(transfer)}
                  >
                    <Icon
                      icon={<ShareIcon />}
                      className={cn(
                        'mr-3 flex h-9 w-9 flex-none items-center justify-center rounded-3xl dark:bg-primary',
                        PaletteUtils.isInsigniaPalette && 'bg-soft-black',
                        PaletteUtils.isVcfcPalette && 'bg-primary',
                      )}
                      color='white'
                    />
                    {PaletteUtils.isInsigniaPalette && (
                      <div className='flex flex-col items-start justify-start'>
                        <Text>
                          {transfer?.beneficiaryAmount} {transfer?.beneficiaryCurrency?.toLocaleString()}
                        </Text>
                        <Text size='xs' color={ColorsEnum.Gray}>
                          {transfer.recipient?.name || transfer.beneficiaryAccount?.name}
                        </Text>
                      </div>
                    )}
                    {PaletteUtils.isVcfcPalette && (
                      <div className='flex flex-col items-start justify-start gap-1.5'>
                        <div className='flex items-center gap-1.5'>
                          <Text className='text-[15px]' weight='semibold'>
                            {transfer?.beneficiaryAmount?.toLocaleString()} {transfer?.beneficiaryCurrency}
                          </Text>
                          <Text className='text-[15px]' color={ColorsEnum.Gray} weight='semibold'>
                            {transfer.recipient?.name || transfer.beneficiaryAccount?.name}
                          </Text>
                        </div>
                        <div className='flex gap-1'>
                          <Text className='text-[10px] text-deep-graphite' weight='semibold'>
                            {DateUtils.formatDate(transfer.createdOn!)}
                          </Text>
                          <Text weight='semibold' className='text-[10px]' color={ColorsEnum.Gray}>
                            {FormatUtils.transactionTypeTransformation(t(`transferRequestType.${transfer.type}`, { ns: 'common' }))}
                          </Text>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Pagination
                noSelect
                totalItemCount={transferStore.data.total}
                currentPage={listState.page}
                countItemPage={listState.count}
                onChangePage={listState.onChangePage}
                onChangeCountItemPage={listState.onChangeCount}
              />
            </>
          )}
        </div>
      )}
      {showApprovalModal && (
        <TransferRequestConfirmModal
          isOpen={!!showApprovalModal}
          onClose={() => setShowApprovalModal(null)}
          onOpenOtpModal={(data) => (setShowOtpModal(data), setShowApprovalModal(null))}
          onCloseOtpModal={() => setShowOtpModal(null)}
          transactionId={showApprovalModal.id}
          transferData={showApprovalModal}
          noDocumentUpload
          onReject={() => ($notificationStore.events.setWarning(t('notifications.rejected')), submitHandler())}
        />
      )}
      <OTPModal
        isOpen={!!showOtpModal}
        onClose={() => setShowOtpModal(null)}
        data={showOtpModal!}
        onSubmit={(data) => (setShowConfirmModal(data!), submitHandler())}
      />
      <ConfirmModal
        isOpen={!!showConfirmModal}
        onClose={() => setShowConfirmModal(null)}
        icon={<ConfirmIcon />}
        title={(showConfirmModal?.totalAmount || 0) >= 50000 ? t('notifications.approved.title2') : t('notifications.approved.title')}
        description={
          <div className='mb-4 flex flex-col gap-2 text-center'>
            <p>{t('notifications.approved.subtitle')}</p>
            {(showConfirmModal?.totalAmount || 0) >= 50000 && <p>{t('notifications.approved.description')}</p>}
          </div>
        }
        buttonTitle='Transaction page'
        onClick={() => navigate(RoutePaths.accountTransaction.onNavigate(showConfirmModal!.paymentId, { back: RoutePaths.payments.onNavigate() }))}
      />
    </>
  );
};
